/*
 * BusinessWalk — Map the Business as a FLOW, not a chat.
 *
 * Two ways through one script, chosen up front: Orbit speaks it, or you type
 * the answers. There is no free-prose door and no composer — the same screen
 * grammar the invited participant's onboarding uses (consent aside — this is
 * the company's own record, not a person's), driven by the business script the
 * backend walks.
 *
 * The website step is the flow's one readable source: hand over the site and
 * the backend reads it into the dossier, so most of what follows is confirming
 * what the site already says rather than answering cold.
 *
 * Voice runs on the participant walk's agent — its tools are CLIENT tools, so
 * they are pointed at the business endpoints here and no agent changes.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, ArrowRight, AudioLines, Check, Ear, Globe, HelpCircle, Keyboard,
  Loader2, Mic, PhoneOff, SkipForward, Sparkles,
} from 'lucide-react';
import { ConversationProvider, useConversation } from '@elevenlabs/react';

import {
  getBusinessWalk, getBusinessWalkVoice, submitBusinessWalk,
  type WalkQuestion, type WalkState,
} from '../lib/intakeApi';

type WalkMode = 'voice' | 'form';

interface BusinessWalkProps {
  clientId?: string | null;
  /** Fired on every answer so the host can refresh the dossier panel. */
  onAfterAnswer?: () => void;
  /** Fired once the walk is finished — the host decides where the person goes. */
  onDone?: () => void;
  /** The two ways on from a mapped business. */
  onSeeBusiness?: () => void;
  onScopeProblem?: () => void;
}

const MIC_AVAILABLE = (() => {
  try { return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia; }
  catch { return false; }
})();

const WEBSITE_FIELD = 'a.website';

// What the voice agent needs from a walk state: the line to speak, the field it
// belongs to, and the values that count as an answer.
function compact(state: WalkState): string {
  if (state.done) return JSON.stringify({ done: true, say: state.say ?? '' });
  const q = state.question;
  return JSON.stringify({
    done: false,
    say: q?.say ?? '',
    question_id: q?.field ?? '',
    type: q?.answer_type ?? 'open',
    options: (q?.options ?? []).map((o) => o.value),
    why: q?.why ?? undefined,
    guess: q?.guess?.display ?? undefined,
  });
}

// A field with options is answered by PICKING, not by typing its vocabulary
// back at us — so this only speaks for the fields that have no options.
function answerHint(q: WalkQuestion): string {
  if (q.answer_type === 'multi') return 'Pick as many as apply.';
  if (q.answer_type === 'number') return 'Type a number.';
  return '';
}

// Does this question offer a fixed set to choose from?
function isChoice(q: WalkQuestion): boolean {
  if (!(q.options || []).length) return false;
  return q.answer_type === 'single' || q.answer_type === 'multi';
}

// What is already on file, as option VALUES — the selection the chips open on.
function guessedValues(q?: WalkQuestion): string[] {
  const raw = q?.guess?.value;
  if (raw === undefined || raw === null || raw === '') return [];
  if (Array.isArray(raw)) return raw.map((v) => String(v));
  return [String(raw)];
}

// A client-tool failure, phrased for the agent to read out. Returning this
// keeps the session alive where throwing would end the call; `retry` tells the
// agent it may ask again rather than moving on and losing the question.
function toolError(e: any, fallback: string): string {
  const detail = e?.response?.data?.detail;
  let say = fallback;
  if (typeof detail === 'string' && detail.trim()) say = detail;
  return JSON.stringify({ ok: false, error: say, retry: true });
}

/* ── voice ───────────────────────────────────────────────────────────── */

const WalkVoiceInner: React.FC<{
  clientId?: string | null;
  onState: (s: WalkState) => void;
  onLeave: () => void;
}> = ({ clientId, onState, onLeave }) => {
  const [phase, setPhase] = useState<'idle' | 'connecting' | 'live' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const fieldRef = useRef<string | null>(null);
  const onStateRef = useRef(onState);
  onStateRef.current = onState;

  const apply = useCallback((s: WalkState): string => {
    fieldRef.current = s.done ? null : (s.question?.field ?? null);
    onStateRef.current(s);
    return compact(s);
  }, []);

  const conversation = useConversation({
    clientTools: {
      // Names match the agent's declared client tools; what they DO is decided
      // here, which is how the participant's agent can walk business facts.
      //
      // NEITHER may throw. A rejected client tool takes the whole session down
      // with it — one 400 from an answer the backend would not accept used to
      // end the call mid-sentence. Failures are handed back as something the
      // agent can say instead, so the conversation survives a bad turn.
      onboarding_next_question: async () => {
        try { return apply(await getBusinessWalk(clientId)); }
        catch (e: any) { return toolError(e, 'I could not load the next question.'); }
      },
      onboarding_submit_answer: async ({ question_id, value }: { question_id?: string; value?: unknown }) => {
        try {
          return apply(await submitBusinessWalk({
            field: question_id || fieldRef.current, value, clientId,
          }));
        } catch (e: any) {
          return toolError(e, 'I could not save that answer.');
        }
      },
    },
    onError: () => { setError('Voice dropped. Switch to the form to carry on.'); setPhase('error'); },
    onDisconnect: () => { setPhase('idle'); onLeave(); },
  });

  const start = useCallback(async () => {
    setError(null);
    setPhase('connecting');
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      const conn = await getBusinessWalkVoice(clientId);
      await conversation.startSession({
        signedUrl: conn.signed_url,
        connectionType: 'websocket',
        dynamicVariables: conn.dynamic_variables,
        overrides: conn.first_message ? { agent: { firstMessage: conn.first_message } } : undefined,
      });
      setPhase('live');
    } catch (e: any) {
      setPhase('error');
      setError(
        e?.name === 'NotAllowedError' || e?.name === 'NotFoundError'
          ? 'Microphone access is needed to talk to Orbit.'
          : (e?.response?.status === 503
            ? 'Voice isn’t set up here — switch to the form instead.'
            : 'Could not start voice. Switch to the form instead.'),
      );
    }
  }, [clientId, conversation]);

  const stop = useCallback(() => {
    try { void conversation.endSession(); } catch { /* ignore */ }
  }, [conversation]);

  useEffect(() => {
    const end = () => { try { void conversation.endSession(); } catch { /* ignore */ } };
    return end;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (phase === 'live') {
    const speaking = conversation.isSpeaking;
    return (
      <div className="aa-walk-voice is-live" role="status" aria-live="polite">
        <span className={`aa-livevoice-orb${speaking ? ' is-speaking' : ''}`}>
          {speaking ? <AudioLines size={14} strokeWidth={2.5} /> : <Ear size={14} strokeWidth={2.5} />}
        </span>
        <span className="aa-livevoice-label">{speaking ? 'Orbit is speaking…' : 'Listening…'}</span>
        <button type="button" className="aa-livevoice-end" onClick={stop}>
          <PhoneOff size={14} strokeWidth={2.5} /> End voice
        </button>
      </div>
    );
  }

  return (
    <div className="aa-walk-voice">
      <button
        type="button"
        className="aa-livevoice-start"
        onClick={start}
        disabled={phase === 'connecting'}
      >
        {phase === 'connecting'
          ? <Loader2 size={16} className="aa-livevoice-spin" />
          : <Mic size={16} strokeWidth={2.5} />}
        {phase === 'connecting' ? 'Connecting…' : 'Answer out loud'}
      </button>
      {error && <span className="aa-livevoice-err">{error}</span>}
    </div>
  );
};

// The SDK needs its provider around the hook, so the voice control carries its
// own — it sits inside the question card rather than in a second box.
const WalkVoice: React.FC<{
  clientId?: string | null;
  onState: (s: WalkState) => void;
  onLeave: () => void;
}> = (props) => (
  <ConversationProvider>
    <WalkVoiceInner {...props} />
  </ConversationProvider>
);


/* ── the walk ────────────────────────────────────────────────────────── */

export const BusinessWalk: React.FC<BusinessWalkProps> = ({
  clientId, onAfterAnswer, onDone, onSeeBusiness, onScopeProblem,
}) => {
  const [state, setState] = useState<WalkState | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState('');
  // Selected option VALUES. One entry for a single-select, many for a multi —
  // one piece of state because the only difference is how many taps stick.
  const [picked, setPicked] = useState<string[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  // The two doors. Null until the person picks one on the welcome card; a
  // returning person resumes on voice wherever there's a mic, and can switch at
  // any question. There is no third door.
  const [mode, setMode] = useState<WalkMode | null>(null);
  // The website step reads the site, which takes a beat and fills a lot — both
  // worth saying out loud rather than showing a silent spinner.
  const [reading, setReading] = useState(false);
  const [readNote, setReadNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setState(await getBusinessWalk(clientId)); setError(null); }
    catch { setError('Could not load your business profile. Try again.'); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { void load(); }, [load]);

  const question = state?.question;
  // Every question arrives already showing what we have: the box holding it, or
  // the matching option already selected. The person edits or re-taps rather
  // than starting from nothing. Keyed on the value as well as the field, so a
  // re-ask after an unreadable answer clears cleanly.
  const prefill = question?.guess?.display ?? '';
  // What is on file, as option values — the one source for both seeding the
  // chips and deciding later whether the person changed anything.
  const seededList = useMemo(() => guessedValues(question), [question]);
  const seededKey = seededList.join('\u0000');
  useEffect(() => {
    setText(question?.guess ? prefill : '');
    setPicked(seededList);
  }, [question?.field, question?.guess, prefill, seededKey, seededList]);

  const applyState = useCallback((next: WalkState) => {
    setState(next);
    setNotice(next.notice?.message ?? null);
    if (next.read?.fields) {
      const host = next.read.url.replace(/^https?:\/\//, '');
      let noun = 'fields';
      if (next.read.fields === 1) noun = 'field';
      setReadNote(
        `I read ${host} and filled in ${next.read.fields} ${noun} — `
        + 'check them as they come up.',
      );
    }
    onAfterAnswer?.();
  }, [onAfterAnswer]);

  // Finishing is not a dead end: the host is told once, and moves the person on
  // (to the problem they scoped, or to the business they just mapped). The card
  // below stays up meanwhile with both ways on, so the recap is still readable
  // and an impatient tap goes immediately.
  const doneFiredRef = useRef(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  useEffect(() => {
    if (!state?.done || doneFiredRef.current) return;
    doneFiredRef.current = true;
    const id = window.setTimeout(() => onDoneRef.current?.(), 2200);
    return () => window.clearTimeout(id);
  }, [state?.done]);

  const answer = useCallback(async (value: unknown, opts?: { skip?: boolean }) => {
    if (busy || !question) return;
    setBusy(true);
    setError(null);
    // Both of these belong to the question being answered, not the next one.
    setNotice(null);
    setReadNote(null);
    if (question.field === WEBSITE_FIELD && !opts?.skip) setReading(true);
    try {
      applyState(await submitBusinessWalk({
        field: question.field, value, skip: opts?.skip, clientId,
      }));
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not save that. Try again.');
    } finally {
      setBusy(false);
      setReading(false);
    }
  }, [busy, question, clientId, applyState]);

  if (loading) {
    return (
      <div className="aa-walk">
        <div className="aa-walk-loading"><Loader2 size={16} className="aa-livevoice-spin" /> Loading…</div>
      </div>
    );
  }
  if (!state) return <div className="aa-walk"><p className="aa-walk-err">{error}</p></div>;

  // ── welcome — the ONE choice: Orbit speaks it, or you type it ───────
  if (state.welcome && !mode && !state.done) {
    const [voiceLabel, formLabel] = state.welcome.buttons || [];
    let voiceSub = 'Needs a microphone — not available in this browser.';
    if (MIC_AVAILABLE) {
      voiceSub = 'Orbit asks the questions out loud and you answer in your own words.';
    }
    return (
      <div className="aa-walk">
        <div className="aa-walk-card aa-walk-card--welcome">
          <h2 className="aa-walk-title">{state.welcome.title}</h2>
          <p className="aa-walk-say">{state.welcome.voice}</p>
          <div className="aa-walk-ways">
            <button
              type="button"
              className="aa-walk-way"
              disabled={!MIC_AVAILABLE}
              onClick={() => setMode('voice')}
            >
              <span className="aa-walk-way-icon"><Mic size={18} strokeWidth={2.25} /></span>
              <span className="aa-walk-way-title">{voiceLabel || 'Talk to Orbit'}</span>
              <span className="aa-walk-way-sub">{voiceSub}</span>
              <span className="aa-walk-way-cta">Start talking <ArrowRight size={14} strokeWidth={2.5} /></span>
            </button>
            <button type="button" className="aa-walk-way" onClick={() => setMode('form')}>
              <span className="aa-walk-way-icon"><Keyboard size={18} strokeWidth={2.25} /></span>
              <span className="aa-walk-way-title">{formLabel || "I'll type instead"}</span>
              <span className="aa-walk-way-sub">
                The same questions, one at a time — you type the answers.
              </span>
              <span className="aa-walk-way-cta">Start the form <ArrowRight size={14} strokeWidth={2.5} /></span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── done ───────────────────────────────────────────────────────────
  if (state.done) {
    return (
      <div className="aa-walk">
        <div className="aa-walk-card aa-walk-card--done">
          <span className="aa-walk-donemark"><Check size={18} strokeWidth={2.5} /></span>
          <p className="aa-walk-say">{state.say}</p>
          <div className="aa-walk-progress">
            <div className="aa-walk-bar"><span style={{ width: '100%' }} /></div>
            <span className="aa-walk-count">
              {state.progress.answered} of {state.progress.total} on file
            </span>
          </div>
          <div className="aa-walk-actions">
            {onSeeBusiness && (
              <button type="button" className="aa-walk-primary" onClick={onSeeBusiness}>
                See my business <ArrowRight size={15} strokeWidth={2.5} />
              </button>
            )}
            {onScopeProblem && (
              <button type="button" className="aa-walk-chip" onClick={onScopeProblem}>
                Scope a problem
              </button>
            )}
          </div>
          <p className="aa-walk-why">Taking you there…</p>
        </div>
      </div>
    );
  }

  if (!question) return <div className="aa-walk"><p className="aa-walk-err">Nothing to ask right now.</p></div>;

  // A returning person has no welcome card to choose on, so the door opens on
  // voice wherever there's a mic — the switch is right there either way.
  let fallbackMode: WalkMode = 'form';
  if (MIC_AVAILABLE) fallbackMode = 'voice';
  const activeMode: WalkMode = mode ?? fallbackMode;

  const { phase, progress } = question;
  const hint = answerHint(question);

  // ONE answer surface, always — the same shape the invited participant's form
  // gets. A field we already have a value for arrives with it IN THE BOX and
  // its provenance above it, so accepting is one tap and correcting is one
  // edit. The old two-step ("is that right?" → yes/no → a second screen to
  // type on) hid both the value's source and the options behind a question
  // the person had no way to judge.
  const guess = question.guess;
  const choosing = isChoice(question);
  // "Edited" means the answer on screen differs from what was on file — for
  // chips that is the selected SET, order-insensitive; for a box, the text.
  let edited = text.trim() !== prefill.trim();
  if (choosing) {
    const a = [...picked].sort().join('\u0000');
    const b = [...seededList].sort().join('\u0000');
    edited = a !== b;
  }

  // The switch always names the door you are NOT in, so it means something.
  let otherMode: WalkMode = 'voice';
  let switchLabel = (
    <><Mic size={14} strokeWidth={2.25} /> Talk to Orbit instead — a natural conversation</>
  );
  if (activeMode === 'voice') {
    otherMode = 'form';
    switchLabel = <><Keyboard size={14} strokeWidth={2.25} /> Type the answers instead</>;
  }

  let boxRows = 2;
  if (question.answer_type === 'open') boxRows = 3;

  // The button says what pressing it will do to the value on screen.
  let continueLabel = 'Continue';
  if (guess && !edited) continueLabel = "Yes, that's right";
  if (guess && edited) continueLabel = 'Save and continue';
  if (busy) continueLabel = 'Saving…';

  let canSubmit = text.trim().length > 0;
  if (choosing) canSubmit = picked.length > 0;
  // An optional question can be passed with nothing in the box. Before this the
  // only way past one was the "skip this one" link, and the walk would not end
  // until it had been found: an org chart URL nobody has held the whole
  // onboarding open.
  const blankOptional = Boolean(question.optional) && !canSubmit;
  if (blankOptional) canSubmit = true;
  if (blankOptional) continueLabel = busy ? 'Saving…' : 'Leave blank';

  let groupRole = 'group';
  if (question.answer_type === 'single') groupRole = 'radiogroup';

  let skipLabel = 'Skip this one';
  if (question.field === WEBSITE_FIELD) skipLabel = "We don't have a website";

  // Tapping an option REPLACES the answer on a single-select and TOGGLES it on a
  // multi. Nothing is sent on the tap: the primary button below is the one
  // commit, so a mis-tap costs a second tap rather than a wrong answer filed.
  const toggle = (value: string) => {
    if (question.answer_type === 'single') { setPicked([value]); return; }
    setPicked((prev) => {
      if (prev.includes(value)) return prev.filter((v) => v !== value);
      return [...prev, value];
    });
  };

  const submitAnswer = () => {
    // An untouched guess is confirmed as it stands, rather than round-tripping
    // what is already on file back through validation.
    if (guess && !edited) { void answer(null); return; }
    if (choosing) {
      if (!picked.length) return;
      if (question.answer_type === 'single') { void answer(picked[0]); return; }
      void answer(picked);
      return;
    }
    const value = text.trim();
    if (!value) {
      // Nothing typed on an optional question is a decision, so record it as
      // one rather than dropping the tap on the floor.
      if (question.optional) void answer(null, { skip: true });
      return;
    }
    void answer(value);
  };

  return (
    <div className="aa-walk">
      {/* Phase header + segmented progress — the same orientation the invited
          participant gets, so both walks read as one product. */}
      <div className="aa-walk-head">
        <span className="aa-walk-phase">
          Phase {phase.index} of {phase.total} · {phase.title.toUpperCase()}
        </span>
        <div className="aa-walk-progress">
          <div className="aa-walk-segs" aria-hidden="true">
            {Array.from({ length: phase.total }).map((_, i) => {
              let cls = '';
              if (i < phase.index) cls = 'is-on';
              return <span key={i} className={cls} />;
            })}
          </div>
          <span className="aa-walk-count">{progress.answered}/{progress.total}</span>
        </div>
      </div>

      <div className="aa-walk-card">
        {/* Exactly one switch, naming the door you are NOT in. */}
        {MIC_AVAILABLE && (
          <button type="button" className="aa-walk-switch" onClick={() => setMode(otherMode)}>
            {switchLabel}
          </button>
        )}

        <div className="aa-walk-q">
          <span className="aa-walk-orb" aria-hidden="true"><Sparkles size={16} strokeWidth={2.25} /></span>
          <div className="aa-walk-q-body">
            <span className="aa-walk-q-who">Orbit</span>
            <p className="aa-walk-say">{question.say}</p>
            {question.why && (
              <p className="aa-walk-why">
                <HelpCircle size={13} strokeWidth={2.25} />
                <span><b>Why I ask —</b> {question.why}</span>
              </p>
            )}
          </div>
        </div>

        {/* Where a prefilled answer came from. Without this, "is that right?"
            is a question about a value with no author. */}
        {guess?.source && (
          <p className="aa-walk-prov">
            <Sparkles size={12} strokeWidth={2.25} /> {guess.source}
          </p>
        )}

        {notice && (
          <p className="aa-walk-notice" role="alert">
            <AlertTriangle size={14} strokeWidth={2.25} /> {notice}
          </p>
        )}

        {readNote && (
          <p className="aa-walk-read" role="status">
            <Globe size={13} strokeWidth={2.25} /> {readNote}
          </p>
        )}

        {reading && (
          <p className="aa-walk-reading" role="status" aria-live="polite">
            <Loader2 size={14} className="aa-livevoice-spin" />
            Reading what you gave me and filling in what it tells me…
          </p>
        )}

        {/* Voice mode: Orbit asks it aloud and you answer aloud. The question
            stays on screen to read, but the typed controls do not — one
            surface, one way to answer. */}
        {!reading && activeMode === 'voice' && (
          <WalkVoice clientId={clientId} onState={applyState} onLeave={() => { void load(); }} />
        )}

        {/* The answer surface. A field with a fixed vocabulary is answered by
            picking from it — with whatever is already on file arriving
            selected. Everything else gets the box. Either way one button
            commits, so the two read as the same question. */}
        {!reading && activeMode === 'form' && (
          <div className="aa-walk-typed">
            {hint && <p className="aa-walk-hint">{hint}</p>}

            {choosing && (
              <div className="aa-walk-answers" role={groupRole}>
                {(question.options || []).map((o) => {
                  const on = picked.includes(o.value);
                  let cls = 'aa-walk-chip';
                  if (on) cls = 'aa-walk-chip is-picked';
                  return (
                    <button
                      key={o.value}
                      type="button"
                      className={cls}
                      disabled={busy}
                      title={o.description}
                      aria-pressed={on}
                      onClick={() => toggle(o.value)}
                    >
                      {on && <Check size={13} strokeWidth={2.75} />}
                      {o.label || o.value}
                    </button>
                  );
                })}
              </div>
            )}

            {!choosing && (
              <textarea
                rows={boxRows}
                value={text}
                autoFocus
                disabled={busy}
                placeholder={question.placeholder || 'Type your answer…'}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitAnswer(); }
                }}
              />
            )}

            <div className="aa-walk-actions">
              <button type="button" className="aa-walk-primary"
                      disabled={busy || !canSubmit} onClick={submitAnswer}>
                {guess && !edited && <Check size={15} strokeWidth={2.5} />}
                {continueLabel}
                {(edited || !guess) && <ArrowRight size={15} strokeWidth={2.5} />}
              </button>
            </div>
          </div>
        )}

        <div className="aa-walk-foot">
          {/* Every optional question keeps its way out, INCLUDING the ones that
              arrive with a value on file. A guess is not consent: the website
              is guessed from the email domain, so a company without one used to
              be trapped — it could not accept the guess, could not skip, and
              "none" is not a URL the field will take. */}
          {!question.required && (
            <button type="button" className="aa-walk-escape" disabled={busy}
                    onClick={() => void answer(null, { skip: true })}>
              <SkipForward size={13} strokeWidth={2.25} /> {skipLabel}
            </button>
          )}
        </div>

        {error && <p className="aa-walk-err">{error}</p>}
      </div>
    </div>
  );
};

export default BusinessWalk;
